import React, { useEffect, useState } from "react";
import { Save, Plus, Trash2, Loader2 } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { toast } from "sonner";
import { api, errText } from "@/lib/devadminApi";

const NEW = () => ({ id: `new-${Date.now()}`, _new: true, key: "", name: "", script_text: "", variables_schema: [] });

export function VoiceScripts({ eventId }) {
  const [rows, setRows] = useState(null);
  const [orig, setOrig] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/events/${eventId}/voice-scripts`).then((r) => {
    setRows(r.data); setOrig(Object.fromEntries(r.data.map((x) => [x.id, x])));
  }).catch((e) => toast.error(errText(e)));
  useEffect(() => { setRows(null); load(); }, [eventId]); // eslint-disable-line

  const upd = (i, k, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const del = async (i) => {
    const r = rows[i];
    if (r._new) return setRows(rows.filter((_, idx) => idx !== i));
    if (!window.confirm(`Delete script "${r.key}"?`)) return;
    try { await api.delete(`/voice-scripts/${r.id}`); toast.success(`${r.key} removed`); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  const save = async () => {
    setBusy(true);
    const ops = rows.map((r) => {
      const key = (r.key || "").trim();
      const name = (r.name || "").trim();
      const script_text = r.script_text || "";
      if (!key || !name || !script_text) return null;
      const vars = typeof r.variables_schema === "string"
        ? r.variables_schema.split(",").map((s) => s.trim()).filter(Boolean)
        : (r.variables_schema || []);
      const body = { key, name, script_text, variables_schema: vars };
      if (r._new) return api.post(`/events/${eventId}/voice-scripts`, body);
      const o = orig[r.id];
      const diff = Object.fromEntries(Object.entries({ name, script_text, variables_schema: vars }).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(o?.[k])));
      return Object.keys(diff).length ? api.patch(`/voice-scripts/${r.id}`, diff) : null;
    }).filter(Boolean);
    if (!ops.length) { setBusy(false); return toast.info("No changes"); }
    const res = await Promise.allSettled(ops);
    const failed = res.filter((x) => x.status === "rejected");
    failed.length ? toast.error(errText(failed[0].reason)) : toast.success(`Voice scripts saved · ${ops.length} change${ops.length > 1 ? "s" : ""}`);
    setBusy(false); load();
  };

  if (!rows) return <div className="mono-label" data-testid="voice-scripts-loading">Loading voice scripts…</div>;

  return (
    <Panel className="p-6">
      <div className="flex items-center justify-between">
        <MonoLabel>Voice Scripts · used by Bolna agent to open conversations</MonoLabel>
        <button data-testid="voice-add-btn" onClick={() => setRows([...rows, NEW()])} className="btn-ghost"><Plus size={14} /> Add script</button>
      </div>
      <div className="mt-4 space-y-6">
        {rows.length === 0 && <div className="mono-label" style={{ color: "var(--taupe)" }}>No scripts yet · click "Add script"</div>}
        {rows.map((r, i) => (
          <div key={r.id} className="hairline p-4" data-testid={`voice-row-${i}`}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="block"><div className="mono-label mb-1">Key</div>
                <input data-testid={`voice-key-${i}`} className="input-x !py-2" value={r.key || ""} disabled={!r._new} placeholder="lower_snake_case" onChange={(e) => upd(i, "key", e.target.value)} /></label>
              <label className="block"><div className="mono-label mb-1">Name</div>
                <input data-testid={`voice-name-${i}`} className="input-x !py-2" value={r.name || ""} onChange={(e) => upd(i, "name", e.target.value)} /></label>
              <label className="block"><div className="mono-label mb-1">Variables (comma-sep)</div>
                <input data-testid={`voice-vars-${i}`} className="input-x !py-2" value={Array.isArray(r.variables_schema) ? r.variables_schema.join(", ") : (r.variables_schema || "")} onChange={(e) => upd(i, "variables_schema", e.target.value)} placeholder="name, event_date, tier_name" /></label>
            </div>
            <label className="block mt-3"><div className="mono-label mb-1">Script text — use {"{{ name }}"} style placeholders</div>
              <textarea data-testid={`voice-text-${i}`} rows={4} className="input-x !py-2 !font-mono !text-xs" value={r.script_text || ""} onChange={(e) => upd(i, "script_text", e.target.value)} /></label>
            <div className="mt-3 text-right">
              <button data-testid={`voice-del-${i}`} onClick={() => del(i)} className="btn-ghost !text-[var(--taupe)] hover:!text-[var(--red)]"><Trash2 size={14} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
      <button data-testid="voice-save-btn" className="btn-red mt-5" disabled={busy} onClick={save}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save scripts
      </button>
    </Panel>
  );
}
